import { useCallback, useEffect, useState } from 'react';
import { createLimitRequest, cancelLimitRequest, listLimitRequestsByHtx, type BackendLimitRequest } from '@/api/lending';
import { ApiError } from '@/api/client';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';
import { EmptyState } from '@/components/base/EmptyState';
import { formatCompactCurrency } from '@/utils/format';
import { getContractsByHtx, getFarmersByHtx } from '@/store';
import { useAuth } from '@/hooks/useAuth';

const AMOUNT_PRESETS = [10_000_000, 20_000_000, 30_000_000, 50_000_000];

/** HTX creating a limit request on behalf of one of its members — backend
 * already allows HTX_STAFF to `create()` (only `decide()` is CO/OPS/ADMIN/
 * DEPT_HEAD/REGIONAL_MANAGER-only), this was just missing from the UI. */
function CreateLimitRequestModal({ htxId, onClose, onCreated }: { htxId: string; onClose: () => void; onCreated: () => void }) {
  const farmers = getFarmersByHtx(htxId);
  const contracts = getContractsByHtx(htxId);
  const [farmerId, setFarmerId] = useState('');
  const [contractId, setContractId] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Feedback 08 B6: this used to list every contract regardless of status —
  // picking a DRAFT/Chờ ký one and submitting hit a 400 ("Hợp đồng chưa gửi
  // hoặc đã đóng vòng đời") with no indication beforehand which ones would work.
  const farmerContracts = contracts.filter((c) => c.farmerId === farmerId && (c.status === 'Đang hiệu lực' || c.status === 'Sắp hết hạn'));

  async function submit() {
    if (!farmerId || !contractId || !amount) return showToast('Chọn nông hộ, hợp đồng và nhập số tiền', 'error');
    setSubmitting(true);
    try {
      await createLimitRequest({ farmerId, contractId, requestedAmountVnd: amount, reason: reason || undefined });
      showToast('Đã gửi yêu cầu hạn mức tới Vùng trồng', 'success');
      onCreated();
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : 'Gửi yêu cầu thất bại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // z-[97]: matches BottomSheet's convention — see CreateLoanForFarmerModal.
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white p-4 space-y-3 max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Tạo yêu cầu hạn mức</h3>
          <button type="button" onClick={onClose} className="text-gray-400"><i className="ri-close-line text-lg" /></button>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nông hộ</label>
          <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={farmerId} onChange={(e) => { setFarmerId(e.target.value); setContractId(''); }}>
            <option value="">— Chọn nông hộ —</option>
            {farmers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hợp đồng</label>
          <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={contractId} onChange={(e) => setContractId(e.target.value)} disabled={!farmerId}>
            <option value="">— Chọn hợp đồng —</option>
            {farmerContracts.map((c) => <option key={c.id} value={c.id}>{c.soHopDong}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Số tiền đề nghị</label>
          <div className="flex flex-wrap gap-2">
            {AMOUNT_PRESETS.map((v) => (
              <button key={v} type="button" onClick={() => setAmount(v)} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${amount === v ? 'border-agrida-600 bg-agrida-600 text-white' : 'border-gray-300 text-gray-700'}`}>
                {(v / 1_000_000).toFixed(0)} triệu
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Lý do</label>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="VD: Mở rộng diện tích canh tác" />
        </div>
        {/* Feedback 08 B7: this stayed enabled with no amount picked — a click
            just silently no-op'd (submit()'s own guard returned before any
            request fired), so it looked broken instead of merely incomplete. */}
        <button type="button" disabled={submitting || !farmerId || !contractId || !amount} onClick={submit} className="w-full rounded-lg bg-agrida-600 text-white text-sm font-semibold py-2.5 disabled:opacity-50">
          {submitting ? 'Đang gửi...' : 'Gửi yêu cầu'}
        </button>
      </div>
    </div>
  );
}

const STATUS_STYLE: Record<BackendLimitRequest['status'], { label: string; cls: string }> = {
  PENDING: { label: 'Chờ duyệt', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  APPROVED: { label: 'Đã duyệt', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  REJECTED: { label: 'Từ chối', cls: 'bg-red-50 text-red-700 border-red-200' },
  CANCELLED: { label: 'Đã hủy', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/**
 * The cooperative's view of its members' limit requests — read-only on purpose.
 *
 * Approving one performs the actual grant through
 * ContractService.grantLimitFromContract, which is restricted to
 * CO/OPS/ADMIN/DEPT_HEAD; HTX_STAFF has no route to it. Showing a decide button
 * here would be an action the backend refuses, so the tab says who decides instead.
 */
export function LimitRequestTab({ htxId }: { htxId?: string }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<BackendLimitRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    if (!htxId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listLimitRequestsByHtx(htxId)
      .then(setRows)
      .catch((e) => showToast(e instanceof ApiError ? e.message : 'Không tải được yêu cầu hạn mức', 'error'))
      .finally(() => setLoading(false));
  }, [htxId]);

  useEffect(load, [load]);

  // Feedback 08 B1: backend already lets whoever filed a request cancel it
  // (limit-request.service.ts's cancel() checks requestedBy === actorId) — the
  // HTX side simply had no button for it, even for a request the HTX itself
  // created via "+ Tạo yêu cầu hạn mức cho nông hộ" above.
  const handleCancel = async (requestId: string) => {
    const ok = await confirmModal({
      title: 'Hủy yêu cầu hạn mức này?',
      message: 'Nông hộ sẽ không nhận được hạn mức từ yêu cầu này nữa.',
      confirmText: 'Hủy yêu cầu',
      cancelText: 'Đóng',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await cancelLimitRequest(requestId);
      showToast('Đã hủy yêu cầu hạn mức', 'success');
      load();
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : 'Hủy yêu cầu thất bại', 'error');
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Đang tải yêu cầu hạn mức...</div>;
  }

  const createButton = htxId && (
    <button
      type="button"
      onClick={() => setCreateOpen(true)}
      className="w-full rounded-xl border border-dashed border-agrida-300 bg-agrida-50 py-2.5 text-sm font-semibold text-agrida-700"
    >
      + Tạo yêu cầu hạn mức cho nông hộ
    </button>
  );
  const createModal = createOpen && htxId && (
    <CreateLimitRequestModal htxId={htxId} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
  );

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {createButton}
        <EmptyState icon="ri-scales-3-line" title="Chưa có yêu cầu hạn mức" description="Yêu cầu tăng hạn mức từ nông hộ sẽ xuất hiện tại đây." />
        {createModal}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {createButton}
      <p className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800">
        Yêu cầu tăng hạn mức do Agrida (phòng tín dụng) quyết định. Hợp tác xã theo dõi và phối hợp xác nhận thông tin nông hộ.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => {
          const style = STATUS_STYLE[r.status];
          return (
            <article key={r._id} data-testid="htx-limit-request" className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="truncate text-sm font-bold text-slate-900">{r.farmerName || 'Nông hộ'}</h4>
                  {/* Feedback 08 B13: a contract missing its own số HĐ used to
                      fall back to the literal word "Hợp đồng", indistinguishable
                      from a contract genuinely named that — fall back to a short
                      id fragment instead, matching contracts.ts's own convention. */}
                  <p className="mt-0.5 truncate text-[11px] text-slate-500">
                    {r.contractNo || `#${r.contractId.slice(-6)}`}
                    {r.createdAt ? ` · ${new Date(r.createdAt).toLocaleDateString('vi-VN')}` : ''}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-semibold ${style.cls}`}>{style.label}</span>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                <div>
                  <dt className="text-slate-400">Đề nghị</dt>
                  <dd className="font-bold text-slate-900">{formatCompactCurrency(Number(r.requestedAmountVnd))}</dd>
                </div>
                <div>
                  <dt className="text-slate-400">Hợp đồng đã cấp</dt>
                  <dd className="text-slate-700">{formatCompactCurrency(Number(r.limitGrantedVnd || 0))}</dd>
                </div>
              </dl>
              {r.reason && <p className="mt-1.5 text-[11px] text-slate-500">Lý do: {r.reason}</p>}
              {r.decisionNote && <p className="mt-1 text-[11px] text-slate-600">Phản hồi: {r.decisionNote}</p>}
              {r.status === 'PENDING' && r.requestedBy === user?.id && (
                <button
                  type="button"
                  onClick={() => handleCancel(r._id)}
                  className="mt-2 text-[11px] font-semibold text-red-500 hover:text-red-600"
                >
                  Hủy yêu cầu
                </button>
              )}
            </article>
          );
        })}
      </div>
      {createModal}
    </div>
  );
}
